import { CSSProperties } from "react";
import videojs, { Player } from "video.js";

declare module "video.js" {
  export interface Player {
    progressMarker: (markers: Marker[], config?: MarkerConfig) => void;
  }
}

// Marker plugin types
export interface MarkerStyleConfig {
  closeButton?: CSSProperties;
  marker?: CSSProperties;
}

export interface MarkerConfig {
  /** Whether to pause the video when a marker is detected. Defaults to false */
  stopOnMarker?: boolean;
  /** Callback function when a marker is detected */
  onDetectedMarker?: (marker: MappedMarker) => void;
  /** Whether to show marker indicators. Defaults to true */
  enabled?: boolean;
  /** Custom styles for marker elements */
  styles?: MarkerStyleConfig;
}

export interface Marker {
  /** Time position of the marker in seconds (can be string or number) */
  time: string | number;
  /** Duration of the marker in seconds (can be string or number) */
  duration?: string | number;
  /** Whether to pause on this specific marker. Overrides global stopOnMarker config */
  stopOnMarker?: boolean;
  /** Whether to show marker indicator for this specific marker. Overrides global marker config */
  enabled?: boolean;
  /** Speaker name associated with the marker */
  speakerName?: string;
}

export interface MappedMarker {
  time: string | number;
  duration: string | number;
  stopOnMarker: boolean;
  enabled?: boolean;
  speakerName: string;
}

export interface MappedMarkerConfig {
  stopOnMarker: boolean;
  onDetectedMarker?: (marker: MappedMarker) => void;
  enabled: boolean;
  styles: {
    closeButton: CSSProperties;
    marker: CSSProperties;
  };
}

function injectMarkerStyles(player: Player) {
  const playerId = player.id();
  const styleId = `vjs-marker-styles-${playerId}`;

  // Only inject styles once per player
  if (document.getElementById(styleId)) {
    return;
  }

  const styleEl = document.createElement("style");
  styleEl.id = styleId;
  styleEl.textContent = `
    #${playerId} .vjs-marker {
      transition: transform 0.15s ease-out;
      transform-origin: center center;
      z-index: 1;
      cursor: pointer;
    }
    #${playerId} .vjs-progress-holder:hover .vjs-marker {
      transform: scaleY(3);
    }
    #${playerId} .vjs-marker-tooltip {
      position: absolute;
      bottom: 100%;
      left: 50%;
      transform: translateX(-50%) scaleY(0.333);
      transform-origin: center center;
      background: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 12px;
      white-space: nowrap;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.15s ease-out;
      margin-bottom: 6px;
    }
    #${playerId} .vjs-marker:hover .vjs-marker-tooltip {
      opacity: 1;
    }
  `;
  document.head.appendChild(styleEl);
}

function registerMarker(
  player: Player,
  markers: MappedMarker[],
  configs: MappedMarkerConfig,
) {
  // Inject hover styles for this player
  injectMarkerStyles(player);

  markers.forEach(marker => {
    let markerEl = videojs.dom.createEl("div", {
      className: "vjs-marker",
      innerHTML: `<div class='vjs-marker-point'></div><div class='vjs-marker-tooltip'>${marker.speakerName}</div>`,
    }) as HTMLDivElement;

    const duration = player.duration() ?? 0;
    const timePct =
      duration > 0 ? (parseFloat(String(marker.time)) / duration) * 100 : 0;
    const widthPct =
      duration > 0 ? (parseFloat(String(marker.duration)) / duration) * 100 : 0;

    const addMarkerIndicator = () => {
      const markerStyle = {
        height: "100%",
        minWidth: "8px",
        background: "#b4befe", // primary.light
        position: "absolute",
        borderRadius: "2px",
        left: `calc(${timePct}% - 4px)`,
        width: `calc(${widthPct}% + 8px)`,
      };
      Object.assign(markerEl.style, markerStyle, configs.styles.marker);
    };

    if (marker?.enabled === undefined) {
      configs?.enabled && addMarkerIndicator();
    } else {
      marker?.enabled && addMarkerIndicator();
    }
    const progressHolderEl = player.el().querySelector(".vjs-progress-holder");
    if (!progressHolderEl) {
      return;
    }
    progressHolderEl.appendChild(markerEl);
  });
}

let timer = 0;
function onTimeUpdate(
  player: Player,
  markers: MappedMarker[],
  configs: MappedMarkerConfig,
) {
  const { stopOnMarker, onDetectedMarker } = configs;

  markers.map(marker => {
    const currentTime = player.currentTime()?.toFixed() ?? 0;
    const positionTime = Number(marker.time).toFixed();
    if (positionTime == currentTime) {
      if (timer === Number(currentTime)) {
        return;
      }

      if (marker?.stopOnMarker === undefined) {
        stopOnMarker && player.pause();
      } else {
        marker?.stopOnMarker && player.pause();
      }

      if (typeof onDetectedMarker == "function") {
        onDetectedMarker(marker);
      }
      timer = Number(currentTime);
    }
  });
}

function configsMapper(data: MarkerConfig): MappedMarkerConfig {
  return {
    stopOnMarker: data?.stopOnMarker ?? false,
    onDetectedMarker: data?.onDetectedMarker ?? undefined,
    enabled: data?.enabled ?? true,
    styles: {
      closeButton: data?.styles?.closeButton ?? {},
      marker: data?.styles?.marker ?? {},
    },
  };
}

function markerMapper(data: Marker): MappedMarker {
  return {
    duration: data?.duration ?? 0,
    time: data?.time ?? 0,
    stopOnMarker: data?.stopOnMarker ?? false,
    enabled: data?.enabled ?? undefined,
    speakerName: data?.speakerName ?? "Your learner",
  };
}

export function progressMarkerPlugin(markers: Marker[], configs: MarkerConfig) {
  const markersMap = markers.map(markerMapper);
  const configsMap = configsMapper(configs);

  this.on("canplay", function () {
    registerMarker(this, markersMap, configsMap);
  });

  this.on("timeupdate", function () {
    onTimeUpdate(this, markersMap, configsMap);
  });
}
